import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';

const emptyForm = {
  type: '',
  agentId: '',
  shopName: '',
  shopLocation: '',
  amount: '',
  notes: ''
};

const Balances = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchBalances = async () => {
    try {
      const response = await axios.get('/api/balances');
      setAgents(response.data.agents || []);
      setShops(response.data.shops || []);
    } catch (fetchError) {
      console.error('Error fetching balances:', fetchError);
      setError(fetchError.response?.data?.message || 'Failed to load balances.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, []);

  if (user?.role !== 'admin') {
    return (
      <div className="card bg-secondary-50">
        <p className="px-6 py-10 text-center text-sm text-secondary-500">
          Only admin can edit agent and shop balances.
        </p>
      </div>
    );
  }

  const openAgentEdit = (agent) => {
    setError('');
    setForm({
      type: 'agent',
      agentId: agent._id,
      shopName: agent.name,
      shopLocation: '',
      amount: String(agent.currentOutstanding ?? 0),
      notes: ''
    });
  };

  const openShopEdit = (shop) => {
    setError('');
    setForm({
      type: 'shop',
      agentId: shop.agent?._id || '',
      shopName: shop.shopName,
      shopLocation: shop.shopLocation || '',
      amount: String(shop.remainingAmount ?? 0),
      notes: ''
    });
  };

  const closeModal = () => {
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (form.type === 'agent') {
        await axios.put(`/api/balances/agents/${form.agentId}`, {
          amount: Number(form.amount),
          notes: form.notes
        });
      } else {
        await axios.put('/api/balances/shops', {
          agent: form.agentId,
          shopName: form.shopName,
          shopLocation: form.shopLocation,
          remainingAmount: Number(form.amount),
          notes: form.notes
        });
      }
      closeModal();
      await fetchBalances();
    } catch (saveError) {
      setError(saveError.response?.data?.message || 'Failed to update balance.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-secondary-900">Balances</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Admin only. Set an agent’s outstanding balance or a shop’s remaining debt.
        </p>
      </div>

      <div className="card bg-secondary-50">
        <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-3">
          <h2 className="text-lg font-semibold text-secondary-900">Agent balances</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Outstanding</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">As of</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {agents.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-sm text-secondary-500">No agents found.</td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-secondary-900">{agent.name}</div>
                      <div className="text-xs text-secondary-500">{agent.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900">
                      {formatCurrency(agent.currentOutstanding)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                      {agent.asOf ? new Date(agent.asOf).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-primary-600 hover:text-primary-700 font-medium" onClick={() => openAgentEdit(agent)}>
                        Edit balance
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-3">
          <h2 className="text-lg font-semibold text-secondary-900">Shop balances</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Shop</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Remaining</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {shops.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-sm text-secondary-500">No shops found.</td>
                </tr>
              ) : (
                shops.map((shop) => (
                  <tr key={`${shop.agent?._id || 'na'}-${shop.shopName}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-secondary-900">{shop.shopName}</div>
                      <div className="text-xs text-secondary-500">{shop.shopLocation || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {shop.agent?.name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900">
                      {formatCurrency(shop.remainingAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        shop.status === 'paid'
                          ? 'bg-success-100 text-success-800'
                          : shop.status === 'partial'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {shop.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-primary-600 hover:text-primary-700 font-medium" onClick={() => openShopEdit(shop)}>
                        Edit balance
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form.type && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-900 bg-opacity-40">
          <div className="relative top-20 mx-auto w-full max-w-md rounded-md border border-secondary-200 bg-white p-5 shadow-lg">
            <h3 className="mb-4 text-lg font-medium text-secondary-900">
              {form.type === 'agent' ? 'Edit agent balance' : 'Edit shop balance'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-md bg-secondary-50 p-3">
                <p className="text-xs font-medium uppercase text-secondary-600">
                  {form.type === 'agent' ? 'Agent' : 'Shop'}
                </p>
                <p className="mt-1 text-sm font-semibold text-secondary-900">{form.shopName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">
                  {form.type === 'agent' ? 'Outstanding balance' : 'Remaining balance'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  value={form.amount}
                  onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Notes</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </div>
              {error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
              )}
              <div className="flex justify-end gap-3">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save balance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Balances;
